import React from 'react';
import { useQueryId, useToggle } from '@/hooks';
import DataTableHeaders from '@/containers/DataTableHeaders';
import { ACTION_DRILLDOWN } from '@/components/HighlightFeature';
import { userAction } from '@/util/helpers/ajax-helpers';
import { transformCellChunksToCells } from '../utils';
import { DrilldownButton } from './DrilldownButton';
import { CellRenderer } from './CellRenderer';
import { DrilldownOptions } from './DrilldownOptions';

const _DrilldownRow: React.FC<any> = ({
  rowIndex,
  dataTable,
  cellChunksForRow,
  isHeaderShown,
  onDrilldown,
}) => {
  const [expanded, toggleExpanded] = useExpandRow();

  const handleDrilldownClick = () => {
    userAction(ACTION_DRILLDOWN);
    onDrilldown(rowIndex);
    /**
     * NOTE: We call toggle in a next tick to avoid a weird UI effect when rows are split before the expansion.
     */
    setTimeout(toggleExpanded, 0);
  };

  return (
    <>
      <tr key={`drilldown-row:${rowIndex}`}>
        <td key="expand" className="no-border">
          {dataTable._drilldownOptions.length > 0 && (
            <DrilldownButton
              handleClick={handleDrilldownClick}
              expanded={expanded}
              rowIndex={rowIndex}
            />
          )}
        </td>
        <CellRenderer
          rowIndex={rowIndex}
          cells={transformCellChunksToCells(cellChunksForRow)}
        />
      </tr>
      {expanded && (
        <DrilldownOptions
          key={`drilldown-options:${rowIndex}`}
          rowIndex={rowIndex}
          dataTable={dataTable}
        />
      )}
      {expanded && isHeaderShown && (
        <DataTableHeaders
          key={`drilldown-header:${rowIndex}`}
          rowIndex={rowIndex}
          headings={dataTable._headings}
          hasOptions={!dataTable._readOnly}
        />
      )}
    </>
  );
};

const useExpandRow = () => {
  const queryId = useQueryId();
  const [expanded, toggleExpanded, resetExpanded] = useToggle(false, true);

  React.useEffect(resetExpanded, [queryId]);

  return [expanded, toggleExpanded] as const;
};

export const DrilldownRow = React.memo(_DrilldownRow);
